import { type ReactNode, useEffect } from 'react';
import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';

import { applyDssBodyClass } from './dss/dss';
import { BrandedNavbar } from './navbar/BrandedNavbar';
import { PageLoading } from './PageLoading';
import { SidebarLogo } from './sidebar/SidebarLogo';
import { applyFavicon } from './skin/apply-favicon';
import { applySkinBodyClass } from './skin/skin-body-class';
import { useShellSkin } from './skin/useShellSkin';

const SIDEBAR_WIDTH = 248;

export interface ShellProps {
  readonly children: ReactNode;
}

/**
 * Moldura enxuta para telas pré-tenant do fluxo de login (ex.: seleção de conta):
 * exibe só a logo da conta numa faixa lateral, sem navbar nem menu. Por não montar
 * useNavbar/useSidebar, não dispara nenhum dos hooks de sessão/tenant do LayoutShell
 * — que, sem tenant, redirecionariam a sessão para o login. Logo, cor e favicon vêm
 * do storage (ou de endpoint público via useShellSkin), nunca de dados com escopo de
 * tenant.
 */
export function BrandedShell({ children }: ShellProps): JSX.Element {
  const { theme, cor, logoHeader, logoHeaderReduzido, faviconRaw } = useShellSkin();

  useEffect(() => {
    applyFavicon(faviconRaw);
  }, [faviconRaw]);

  // Chrome e DSS são mutuamente exclusivos: só o DssShell aplica classes dss-style-*.
  useEffect(() => {
    applyDssBodyClass(false, null);
    return () => {
      document.body.classList.remove('not-dss-style');
    };
  }, []);

  // A folha Angular consome as variáveis --default-color* das classes body-<cor>
  // (_theme-colors.scss). Sem elas, botões como .btn-pri1 perdem a cor da conta.
  useEffect(() => {
    applySkinBodyClass(cor);
  }, [cor]);

  return (
    <ThemeProvider theme={theme}>
      <Box
        className="branded-shell"
        sx={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          flexDirection: 'row',
          height: '100vh',
          overflow: 'hidden',
        }}
      >
        {/* Faixa lateral só com a logo: reproduz a coluna da sidebar do LayoutShell
            (cor da conta), mas sem menu nem os hooks que ele carrega. */}
        <Box
          component="aside"
          data-slot="sidebar"
          sx={{
            flexShrink: 0,
            width: SIDEBAR_WIDTH,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
          }}
        >
          <SidebarLogo
            logoHeader={logoHeader}
            logoHeaderReduzido={logoHeaderReduzido}
            collapsed={false}
            onLogoClick={() => undefined}
          />
        </Box>
        {/* Coluna direita: navbar reduzida (só idioma + logout) e, abaixo, o conteúdo.
            `position: relative` ancora o overlay de carregamento à coluna, deixando a
            faixa da logo de fora. */}
        <Box
          sx={{
            position: 'relative',
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            p: '15px',
          }}
        >
          <Box sx={{ flexShrink: 0 }}>
            <BrandedNavbar />
          </Box>
          {/* Único ponto de rolagem: o conteúdo fica contido aqui, sem vazar scroll
              para a página. O remonte por navegação é decidido por quem fornece o
              conteúdo, igual ao LayoutShell. */}
          <Box
            component="main"
            className="branded-shell__content"
            sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}
          >
            {children}
          </Box>
          <PageLoading contained />
        </Box>
      </Box>
    </ThemeProvider>
  );
}
